import React from "react";
import { Box } from "@mui/material";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ErrorBar,
  CartesianGrid,
  Tooltip,
} from "recharts";

const MAX_SCORE = 800;

const ABBREVIATIONS = {
  "Information and Ideas": "I&I",
  "Craft and Structure": "C&S",
  "Expression of Ideas": "EI",
  "Standard English Conventions": "SEC",
  "Algebra": "Alg",
  "Advanced Math": "Adv",
  "Problem-Solving and Data Analysis": "PSDA",
  "Geometry and Trigonometry": "Geo",
};

const round10 = (n) => Math.round(n / 10) * 10;

const PillarTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const { score, low, high } = payload[0].payload;

  return (
    <div
      style={{
        background: "rgba(255,255,255,0.95)",
        border: "1px solid rgba(0,0,0,0.15)",
        borderRadius: 8,
        padding: "10px 12px",
        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
        fontSize: 13,
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 6 }}>{label}</div>
      <div>
        Estimate: <strong>{round10(score)}</strong>
      </div>
      <div>
        Range: <strong>{round10(low)}–{round10(high)}</strong>
      </div>
    </div>
  );
};

const PillarPerformanceGraph = ({ pillars = [], onPillarClick }) => {
  const data = pillars.map((p) => ({
    id: p.id,
    name: p.name,
    score: p.score,
    error: [p.score - p.low, p.high - p.score],
    low: p.low,
    high: p.high,
  }));

  const handleBarClick = (bar) => {
    const id = bar?.payload?.id;
    if (!id) return;

    onPillarClick?.(id);

    // ✅ kill any focus ring Chrome applies after click
    requestAnimationFrame(() => {
      if (document.activeElement && typeof document.activeElement.blur === "function") {
        document.activeElement.blur();
      }
    });
  };

  return (
    <Box
      // ✅ prevents the chart container from receiving focus on click
      onMouseDown={(e) => e.preventDefault()}
      sx={{
        "& .recharts-wrapper, & .recharts-surface": { outline: "none" },
        "& *:focus, & *:focus-visible": { outline: "none", boxShadow: "none" },
      }}
    >
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={data} margin={{ left: 8, right: 16, bottom: 24 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />

          <XAxis
            dataKey="name"
            tick={{ fontSize: 12 }}
            tickMargin={8}
            tickFormatter={(value) => ABBREVIATIONS[value] ?? value}
          />

          <YAxis domain={[0, MAX_SCORE]} ticks={[0, 200, 400, 600, 800]} />

          <Tooltip content={<PillarTooltip />} cursor={false} />

          <Bar
            dataKey="score"
            fill="#1976d2"
            radius={[6, 6, 0, 0]}
            onClick={handleBarClick}
            style={{ cursor: "pointer" }}
            isAnimationActive={false}
            activeBar={false} // ✅ removes Recharts “active” outline
          >
            <ErrorBar
              dataKey="error"
              direction="y"
              stroke="#000"
              strokeWidth={2}
              strokeOpacity={1}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default PillarPerformanceGraph;
